import { PredefinedFileTags, isSoloFrame } from '@lite-workspace/dto-fotoclass-photo';
import type { StudentDownloadQuality, StudentDownloadScopes, StudentDownloadSettings } from './student-download.dto';
import { resolveStudentDownload } from './student-download.dto';

/** Классификация фото относительно СМОТРЯЩЕГО ученика. 'unknown' = данных не хватает → скачивание закрыто. */
export type StudentPhotoKind = 'ownSolo' | 'foreignSolo' | 'withStudent' | 'withoutStudent' | 'unknown';

/**
 * Единый вход политики скачивания ученика — обе поверхности (редактор, галерея выбора)
 * и сервер (выдача наборов, архив) считают вердикт ТОЛЬКО через эти функции.
 * Fail-closed: любое неизвестное состояние (демо-статус, роль, лица, утверждение) закрывает скачивание.
 */
export interface StudentDownloadContext {
  settings: Partial<StudentDownloadSettings> | undefined;
  /** Демо-проект — глобальный запрет; undefined (статус неизвестен) тоже закрывает. */
  isDemoProject: boolean | undefined;
  /** Поверхность обязана ПОЛОЖИТЕЛЬНО знать, что обслуживает ученика. */
  role: 'student' | 'unknown';
  /** Утверждение альбома; читается только в режиме afterApproval. */
  albumApproved?: boolean;
  /** Ключи изображений утверждённого альбома (обложка + развороты); обязательны per-photo в afterApproval. */
  approvedImageKeys?: ReadonlySet<string> | null;
}

/**
 * Проектный переключатель «знак включён». При открытом скачивании знак живёт только в режиме
 * «Вошедшие в альбом»: сервер ставит его на фото, которые ЭТОТ ученик скачать не может, а альбомные
 * очищаются после утверждения. В режиме «Все из галереи» знака нет — всё скачиваемое чистое, а сервер
 * там кадры не различает. Скачивание выключено — знак берётся из сохранённого `withWatermark`, этот
 * ключ при переключении скачивания не переписывается.
 */
export function isWatermarkActive(input: {
  withWatermark: boolean | undefined;
  studentDownload: Partial<StudentDownloadSettings> | undefined;
  isDemoProject?: boolean | undefined;
}): boolean {
  // Демо-эталон под знаком всегда — его кадры принадлежат витрине, а не проекту.
  if (input.isDemoProject === true) return true;
  const download = resolveStudentDownload(input.studentDownload);
  if (download.enabled) return download.when === 'afterApproval' && download.watermark === true;
  return input.withWatermark ?? true;
}

/** Мастер-гейт: настройка включена + не демо + ученик + when-условие выполнено. */
export function isDownloadGateOpen(ctx: StudentDownloadContext): boolean {
  if (ctx.role !== 'student') return false;
  if (ctx.isDemoProject !== false) return false;
  const settings = resolveStudentDownload(ctx.settings);
  if (!settings.enabled) return false;
  if (settings.when === 'afterApproval' && ctx.albumApproved !== true) return false;
  return true;
}

/**
 * Утверждённый альбом — сам по себе выборка фотографа, поэтому в этом режиме наборы
 * не сужают её дальше: ученик скачивает ровно то, что видит в альбоме, портреты одноклассников тоже.
 */
export function isAlbumOnlyDownload(settings: Partial<StudentDownloadSettings> | undefined): boolean {
  return resolveStudentDownload(settings).when === 'afterApproval';
}

/**
 * Утверждённый альбом отдаётся без знака только пока скачивание ОТКРЫТО в режиме «Вошедшие в альбом»:
 * выключенное скачивание хранит `when`, но знак с альбома не снимает.
 */
export function isApprovedAlbumClean(settings: Partial<StudentDownloadSettings> | undefined): boolean {
  const download = resolveStudentDownload(settings);
  return download.enabled && download.when === 'afterApproval';
}

/** Наборы (`scopes`) из UI ушли — сохранённые флаги мёртвые, любой бакет открыт вместе с гейтом. */
export function isScopeDownloadable(ctx: StudentDownloadContext, _scope: keyof StudentDownloadScopes): boolean {
  return isDownloadGateOpen(ctx);
}

/**
 * Ось ПРИВАТНОСТИ, отдельная от права скачивания: чужой ли это кадр для смотрящего ученика.
 * Право скачивания (`canDownloadPhoto`) сужают ещё и настройки проекта — выключенный гейт, режим
 * «только альбомные», наборы; приватность от них не зависит. Ученик листает крупно ВЕСЬ свой
 * разворот, включая портреты одноклассников, и закрыт у него только чужой файл — пока политика не
 * разрешила его скачать (кадр утверждённого альбома в режиме «Вошедшие в альбом»).
 *
 * `foreignSolo` — чужое соло по определению. `unknown` — про кадр не хватает данных (его нет в
 * выдаче панели ученика), и здесь, как и во всей этой политике, неизвестность закрывается.
 */
export function isForeignStudentPhoto(kind: StudentPhotoKind): kind is 'foreignSolo' | 'unknown' {
  return kind === 'foreignSolo' || kind === 'unknown';
}

/**
 * «Все из галереи» — ученик скачивает всё, что видит, кроме чужих соло. «Вошедшие в альбом» — кадры утверждённого
 * альбома, кроме сольного портрета другого ученика на личном развороте (набор ключей собирает вызывающий).
 */
export function canDownloadPhoto(ctx: StudentDownloadContext, photo: { kind: StudentPhotoKind; imageKey?: string }): boolean {
  if (!isDownloadGateOpen(ctx)) return false;
  if (isAlbumOnlyDownload(ctx.settings)) {
    if (!ctx.approvedImageKeys || !photo.imageKey) return false;
    return ctx.approvedImageKeys.has(photo.imageKey);
  }

  return !isForeignStudentPhoto(photo.kind);
}

/**
 * Архив собирается только из скачиваемых фото, а они всегда чистые — знак защищает лишь то, что ученику
 * скачать нельзя (вне утверждённого альбома), поэтому включённый знак архив не закрывает.
 */
export function isBulkDownloadAvailable(ctx: StudentDownloadContext): boolean {
  return isDownloadGateOpen(ctx) && resolveStudentDownload(ctx.settings).bulk;
}

/**
 * Купленное (оплачено + отретушировано) — собственность ученика: скачивается при любой настройке скачивания,
 * как чистый просмотр в file-proxy. Закрыто только демо и не-ученику; сам набор ключей собирает вызывающий.
 */
export function canDownloadBought(ctx: StudentDownloadContext): boolean {
  return ctx.role === 'student' && ctx.isDemoProject === false;
}

export function availableDownloadQualities(
  settings: Partial<StudentDownloadSettings> | undefined,
): Exclude<StudentDownloadQuality, 'both'>[] {
  const quality = resolveStudentDownload(settings).quality;
  return quality === 'both' ? ['web', 'original'] : [quality];
}

const NO_FACES_TAG: string = PredefinedFileTags.NO_FACES;

/**
 * Классификация по СЕРВЕРНОМУ бакету галереи ученика: фильтры бакетов гарантируют принадлежность
 * (portraits/withMe — только файлы самого ученика), поэтому любое соло в «Без меня» — чужое.
 */
export function classifyServerBucketPhoto(
  bucket: keyof StudentDownloadScopes,
  tags: readonly string[] | undefined,
): StudentPhotoKind {
  if (bucket === 'portraits') return 'ownSolo';
  if (bucket === 'withMe') return 'withStudent';
  return isSoloFrame({ tags }) ? 'foreignSolo' : 'withoutStudent';
}

/**
 * Классификация фото для ученика. facePersonIds === undefined означает «лица не загружены» —
 * соло-кадр тогда 'unknown' (fail-closed), группу без данных о лицах тоже закрываем.
 */
export function classifyStudentPhoto(input: {
  tags: readonly string[];
  /** Счёт лиц кадра; на проекте без меток это единственный сигнал соло. */
  faceCount?: number | null;
  facePersonIds: readonly string[] | undefined;
  studentPersonId: string | undefined;
}): StudentPhotoKind {
  const { tags, faceCount, facePersonIds, studentPersonId } = input;
  const isSolo = isSoloFrame({ tags, faceCount });
  // Соло проверяется ДО no-faces: портрет с нераспознанными лицами обязан остаться закрытым, не «безлюдным».
  if (!isSolo && tags.includes(NO_FACES_TAG)) return 'withoutStudent';
  // Ни одного живого лица и ни одной метки об этом: кто на кадре — неизвестно. Закрываем, как и листинг
  // «без меня»: на проекте без разметки это чаще нераспознанный чужой портрет, чем безлюдный кадр.
  if (faceCount === 0) return 'unknown';
  if (facePersonIds === undefined || studentPersonId === undefined) return 'unknown';
  const hasStudent = facePersonIds.includes(studentPersonId);
  if (isSolo) {
    // Чужое соло — любой одиночный портрет не самого ученика, роль персоны не важна: ученик не видит
    // ничьих соло, кроме своих (учитель — тоже).
    return hasStudent ? 'ownSolo' : 'foreignSolo';
  }

  if (facePersonIds.length === 0) return 'withoutStudent';
  return hasStudent ? 'withStudent' : 'withoutStudent';
}
